import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Globe } from "lucide-react";

const languages = [
    { code: "en", name: "English", flag: "🇬🇧" },
    { code: "es", name: "Español", flag: "🇪🇸" },
];

export default function LanguageSelector() {
    const { i18n } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const changeLanguage = (lng: string) => {
        i18n.changeLanguage(lng);
        setIsOpen(false);
    };

    // Match on the base language: a browser reports "es-ES" or "en-GB", and an
    // exact comparison silently falls through to the first entry — which on a
    // public page means a Spanish visitor sees the English flag above a page
    // rendered in Spanish.
    const base = (i18n.language || "").split("-")[0].toLowerCase();
    const currentLang = languages.find((l) => l.code === base) || languages[0];

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition text-content-secondary"
            >
                <Globe className="w-5 h-5" />
                <span className="hidden sm:inline text-sm font-medium">{currentLang.flag}</span>
            </button>

            {isOpen && (
                <div className="absolute right-0 mt-2 w-40 bg-surface-raised border border-border-subtle rounded-lg shadow-lg py-1 z-50">
                    {languages.map((lang) => (
                        <button
                            key={lang.code}
                            onClick={() => changeLanguage(lang.code)}
                            className={`flex items-center gap-3 w-full px-4 py-2 text-sm text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition
                ${base === lang.code ? "font-semibold text-brand-600 dark:text-brand-400" : "text-content-secondary"}
              `}
                        >
                            <span>{lang.flag}</span>
                            {lang.name}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
